import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useState } from 'react';

const drawerWidth = 260;

const menuItems = [
    { id: 'dashboard', text: 'Панель управления', icon: <DashboardIcon />, color: '#6366f1', roles: ['owner', 'admin', 'manager', 'staff', 'viewer'] },
    { id: 'visits', text: 'Посещения', icon: <PeopleIcon />, color: '#667eea', roles: ['owner', 'admin', 'manager', 'viewer'] },
    { id: 'employee-calendar', text: 'Календарь смен', icon: <AccessTimeIcon />, color: '#06b6d4', roles: ['owner', 'admin', 'manager'] },
    { id: 'statistics', text: 'Статистика', icon: <BarChartIcon />, color: '#14b8a6', roles: ['owner', 'admin', 'manager'] },
    { id: 'cash-journal', text: 'Кассовый журнал', icon: <PointOfSaleIcon />, color: '#059669', roles: ['owner', 'admin', 'manager'] },
    { id: 'settings', text: 'Настройки CRM', icon: <SettingsIcon />, color: '#64748b', roles: ['owner', 'admin', 'manager'] },
    { id: 'menu', text: 'Меню', icon: <RestaurantMenuIcon />, color: '#ec4899', roles: ['owner', 'admin', 'manager'] },
    { id: 'souvenirs', text: 'Сувенирка', icon: <CardGiftcardIcon />, color: '#a855f7', roles: ['owner', 'admin', 'manager'] },
    { id: 'profile', text: 'Мой профиль', icon: <AccountCircleIcon />, color: '#667eea', roles: ['staff'] },
    { id: 'employees', text: 'Сотрудники', icon: <PersonIcon />, color: '#10b981', roles: ['owner', 'manager'] },
    { id: 'payroll', text: 'Расчет зарплаты', icon: <MonetizationOnIcon />, color: '#f59e0b', roles: ['owner'] },
    { id: 'yandex-tickets', text: 'Яндекс.Билеты', icon: <ConfirmationNumberIcon />, color: '#0ea5e9', roles: ['owner', 'viewer'] },
];

const Layout = ({ children, activeSection, onSectionChange, user, onLogout }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);

    // Фильтруем меню по ролям
    const availableMenuItems = user ? menuItems.filter(item =>
        !item.roles || item.roles.includes(user.role)
    ) : menuItems;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuClick = (sectionId) => {
        onSectionChange(sectionId);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const drawer = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            color: 'white',
            overflow: 'hidden'
        }}>
            <Toolbar sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                justifyContent: 'center',
                py: 2,
                flexShrink: 0
            }}>
                <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
                    🏛️ Музей на ощупь
                </Typography>
            </Toolbar>
            {/* Информация о пользователе */}
            {user && (
                <Box sx={{
                    px: 2,
                    pt: 2,
                    pb: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountCircleIcon sx={{ fontSize: 40, color: '#667eea' }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {user.fullName || user.username}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {user.role === 'owner' ? 'Владелец' :
                                    user.role === 'admin' ? 'Администратор' :
                                        user.role === 'manager' ? 'Менеджер' :
                                            user.role === 'staff' ? 'Сотрудник' : 'Наблюдатель'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            <List sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2,
                pt: 2,
                pb: 1,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'rgba(255,255,255,0.25)',
                    borderRadius: 3
                }
            }}>
                {availableMenuItems.map((item) => (
                    <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            selected={activeSection === item.id}
                            onClick={() => handleMenuClick(item.id)}
                            sx={{
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&.Mui-selected': {
                                    background: `linear-gradient(135deg, ${item.color}dd 0%, ${item.color}99 100%)`,
                                    color: 'white',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${item.color}ee 0%, ${item.color}aa 100%)`,
                                    },
                                },
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateX(5px)',
                                },
                            }}
                        >
                            <ListItemIcon sx={{
                                color: activeSection === item.id ? 'white' : item.color,
                                minWidth: 40
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontWeight: activeSection === item.id ? 600 : 500,
                                    fontSize: '0.9rem',
                                    noWrap: true
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}

                {/* Кнопка выхода */}
                {user && onLogout && (
                    <ListItem disablePadding sx={{ mt: 1.5, mb: 0.5 }}>
                        <ListItemButton
                            onClick={onLogout}
                            sx={{
                                borderRadius: 2,
                                border: '1px solid rgba(239, 68, 68, 0.5)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    borderColor: '#ef4444',
                                },
                            }}
                        >
                            <ListItemIcon sx={{ color: '#ef4444', minWidth: 40 }}>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Выход"
                                primaryTypographyProps={{
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    color: '#ef4444'
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                )}
            </List>

            <Box sx={{
                flexShrink: 0,
                py: 1.5,
                px: 2,
                textAlign: 'center',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                opacity: 0.6
            }}>
                <Typography variant="caption" sx={{ color: 'white' }}>
                    © 2025 Touch World
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
                        {menuItems.find(item => item.id === activeSection)?.text || 'Панель управления'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {new Date().toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, overflow: 'hidden' },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            border: 'none',
                            overflow: 'hidden'
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                }}
            >
                <Toolbar />
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default Layout;
